import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { InterviewSubmitResponse } from 'src/app/pages/virtual-interview/models/virtual-interview-model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CompanyVrServiceService {
  private readonly baseUrl = environment.apiUrl;
  constructor(private http: HttpClient) {}
  getSessionData(id: string): Observable<any> {
    return this.http.get(this.baseUrl + 'api/b2b/interview/session/' + id);
  }
  joinInterview(id: string) {
    return this.http.post(this.baseUrl + `api/b2b/interview/${id}/join`, '');
  }
  explainQuestion(sessionId: string, questionId: string) {
    // {{baseUrl}}/api/b2b/interview/{{b2bSessionId}}/explain/<INSERT_QUESTION_VIDEO_ID>
    return this.http.post(
      this.baseUrl + `api/b2b/interview/${sessionId}/explain/${questionId}`,
      '',
    );
  }
  initiateRecording(
    sessionId: string,
    interviewVideoId: string,
  ): Observable<string> {
    const params = {
      sessionId,
      interviewVideoId,
    };

    return this.http
      .post(`${this.baseUrl}api/b2b/interview/initiate-recording`, null, {
        params,
        responseType: 'text',
      })
      .pipe(
        map((response) => {
          return response.replace(/"/g, '');
        }),
      );
  }
  appendChunk(
    videoId: string,
    chunk: Blob,
    currentTotalDuration: number,
    chunkIndex: number,
    attempt: number = 0,
  ): Observable<void> {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 1500;

    const formData = new FormData();
    const cleanVideoId = videoId.replace(/"/g, '');
    const durationInSeconds = Math.floor(currentTotalDuration / 1000);
    const videoBlob = new Blob([chunk], { type: 'video/webm' });

    formData.append('videoId', cleanVideoId);
    formData.append('chunk', videoBlob, `chunk_${chunkIndex}.webm`);
    formData.append('currentTotalDuration', durationInSeconds.toString());
    formData.append('chunkIndex', chunkIndex.toString());

    return this.http
      .post<void>(`${this.baseUrl}api/b2b/interview/append-chunk`, formData)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Dig into every possible location the message could be
          const raw = [
            error?.error?.message,
            error?.error?.error,
            error?.error,
            error?.message,
          ]
            .map((v) => (typeof v === 'string' ? v : JSON.stringify(v ?? '')))
            .join(' ');

          const isOrderError = /order/i.test(raw);

          console.log(
            'Chunk upload error raw:',
            raw,
            '| isOrderError:',
            isOrderError,
          );

          if (isOrderError && attempt < MAX_RETRIES) {
            console.warn(
              `Chunk ${chunkIndex} order error – retrying (${attempt + 1}/${MAX_RETRIES}) in ${RETRY_DELAY_MS}ms...`,
            );
            return new Observable<void>((observer) => {
              setTimeout(() => {
                this.appendChunk(
                  videoId,
                  chunk,
                  currentTotalDuration,
                  chunkIndex,
                  attempt + 1,
                ).subscribe({
                  next: () => {
                    observer.next();
                    observer.complete();
                  },
                  error: (err) => observer.error(err),
                });
              }, RETRY_DELAY_MS);
            });
          }

          return throwError(() => error);
        }),
      );
  }
  appendAdditionalChunk(
    videoId: string,
    chunk: Blob,
    currentTotalDuration: number,
    chunkIndex: number,
    attempt: number = 0,
  ): Observable<void> {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 1500;

    const formData = new FormData();
    const cleanVideoId = videoId.replace(/"/g, '');
    const durationInSeconds = Math.floor(currentTotalDuration / 1000);
    const videoBlob = new Blob([chunk], { type: 'video/webm' });

    formData.append('videoId', cleanVideoId);
    formData.append('chunk', videoBlob, `chunk_${chunkIndex}.webm`);
    formData.append('currentTotalDuration', durationInSeconds.toString());
    formData.append('chunkIndex', chunkIndex.toString());

    return this.http
      .post<void>(
        `${this.baseUrl}api/b2b/interview/append-chunk-additional`,
        formData,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const raw = [
            error?.error?.message,
            error?.error?.error,
            error?.error,
            error?.message,
          ]
            .map((v) => (typeof v === 'string' ? v : JSON.stringify(v ?? '')))
            .join(' ');

          const isOrderError = /order/i.test(raw);

          console.log(
            'Additional chunk upload error raw:',
            raw,
            '| isOrderError:',
            isOrderError,
          );

          if (isOrderError && attempt < MAX_RETRIES) {
            console.warn(
              `Additional chunk ${chunkIndex} order error – retrying (${attempt + 1}/${MAX_RETRIES}) in ${RETRY_DELAY_MS}ms...`,
            );
            return new Observable<void>((observer) => {
              setTimeout(() => {
                this.appendAdditionalChunk(
                  videoId,
                  chunk,
                  currentTotalDuration,
                  chunkIndex,
                  attempt + 1,
                ).subscribe({
                  next: () => {
                    observer.next();
                    observer.complete();
                  },
                  error: (err) => observer.error(err),
                });
              }, RETRY_DELAY_MS);
            });
          }

          return throwError(() => error);
        }),
      );
  }
  finishRecording(videoId: string): Observable<void> {
    const cleanVideoId = videoId.replace(/"/g, '');

    return this.http
      .post<void>(`${this.baseUrl}api/b2b/interview/${cleanVideoId}/finish`, {})
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Finish recording API error:', error);
          // Don't block user flow if finish fails
          return of(void 0);
        }),
      );
  }

  submitInterview(sessionId: string): Observable<InterviewSubmitResponse> {
    return this.http.post<InterviewSubmitResponse>(
      `${this.baseUrl}api/b2b/interview/${sessionId}/submit`,
      {},
    );
  }
}
